import React, { useState } from 'react';
import { useSupportSession } from '../../context/SupportSessionContext';
import SupportEscalationModal from './SupportEscalationModal';

function SupportSessionBanner({ theme = 'light' }) {
    const [showEscalationModal, setShowEscalationModal] = useState(false);
    const {
        activeGrant,
        isSupportMode,
        timeRemaining,
        secondsRemaining,
        exitSupportSession,
        isAnnotationMode,
        toggleAnnotationMode,
        annotations,
        clearAnnotations,
        escalateSupportSession
    } = useSupportSession();

    if (!isSupportMode || !activeGrant) {
        return null;
    }

    const isUrgent = secondsRemaining > 0 && secondsRemaining < 300; // Less than 5 mins
    const orgLabel = activeGrant.org_name || activeGrant.org_id;

    return (
        <>
        <aside
            aria-label="Support session alert"
            className={`w-full px-4 py-2.5 z-40 flex flex-wrap items-center justify-between gap-3 shadow-md border-b text-sm transition-colors ${
                isUrgent
                    ? 'bg-red-700 border-red-800 text-white animate-pulse'
                    : 'bg-amber-600 border-amber-700 text-white'
            }`}
        >
            <div className="flex items-center gap-3 flex-wrap">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/20 text-xs font-bold">
                    🛡️
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">
                        Viewing <span className="underline font-bold">{orgLabel}</span> under Support Grant
                    </span>
                    {activeGrant.reason && (
                        <span className="text-white/90 text-xs px-2 py-0.5 rounded bg-black/20 truncate max-w-md" title={activeGrant.reason}>
                            &ldquo;{activeGrant.reason}&rdquo;
                        </span>
                    )}
                    <span className="bg-black/30 text-xs font-medium px-2.5 py-0.5 rounded-full border border-white/20 uppercase tracking-wider">
                        Read-Only Support Session
                    </span>
                </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap ml-auto">
                <div className="flex items-center gap-1.5 font-mono text-xs font-semibold bg-black/25 px-3 py-1 rounded-md border border-white/20">
                    <span>⏱️</span>
                    <span>Expires in: {timeRemaining}</span>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => toggleAnnotationMode()}
                        title="Toggle live visual annotation pointer tool"
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                            isAnnotationMode
                                ? 'bg-white text-amber-900 shadow-inner ring-2 ring-white font-bold'
                                : 'bg-white/20 hover:bg-white/30 text-white'
                        }`}
                    >
                        <span>📍</span>
                        <span>{isAnnotationMode ? 'Annotating (Click screen)' : 'Add Annotation'}</span>
                    </button>

                    {annotations.length > 0 && (
                        <button
                            type="button"
                            onClick={clearAnnotations}
                            title="Clear all active live annotations"
                            className="px-2.5 py-1 text-xs font-medium rounded-md bg-black/20 hover:bg-black/40 text-white/90 transition-colors"
                        >
                            Clear Pins ({annotations.length})
                        </button>
                    )}

                    {activeGrant.escalated_issue_url ? (
                        <a
                            href={activeGrant.escalated_issue_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 text-xs font-bold rounded-md bg-white text-blue-700 hover:bg-blue-50 shadow transition-colors"
                        >
                            View Escalation
                        </a>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setShowEscalationModal(true)}
                            className="px-3 py-1 text-xs font-bold rounded-md bg-red-800/80 text-white hover:bg-red-900 shadow transition-colors"
                        >
                            Escalate to Engineering
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={() => {
                            if (window.confirm('Exit this live support session and return to platform console?')) {
                                exitSupportSession('Support session closed by operator.', { revoke: true });
                            }
                        }}
                        className="px-3 py-1 text-xs font-bold rounded-md bg-white text-red-700 hover:bg-red-50 shadow transition-colors"
                    >
                        Exit Session
                    </button>
                </div>
            </div>
        </aside>
        {showEscalationModal && (
            <SupportEscalationModal
                activeGrant={activeGrant}
                annotations={annotations}
                dark={theme === 'dark'}
                onClose={() => setShowEscalationModal(false)}
                onSubmit={escalateSupportSession}
            />
        )}
        </>
    );
}

export default SupportSessionBanner;
